import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Card, PageHeader, SelectInput, TextInput, useEntityList } from "@pericles/framework-frontend";

import {
  assignRoadmapSegmentUseCase,
  applyRoadmapMapping,
  createRoadmapSegment,
  deleteRoadmapSegment,
  createRoadmapProject,
  fetchRoadmapCollaborationGroups,
  fetchRoadmapCollaborationRuntimeVerification,
  fetchRoadmapAnalysisFields,
  fetchRoadmapData,
  fetchRoadmapMappingSuggestion,
  fetchRoadmapProjects,
  fetchRoadmapQualityAudit,
  fetchRoadmapSegments,
  fetchRoadmapSettings,
  fetchRoadmapSynthesis,
  importRoadmapFromAirtable,
  isMigrationsRequiredError,
  overrideRoadmapQualityAudit,
  removeRoadmapSegmentUseCase,
  saveRoadmapData,
  saveRoadmapAnalysisFields,
  runRoadmapQualityAudit,
  runRoadmapSynthesis,
  provisionRoadmapCollaborationGroups,
  saveRoadmapCollaborationGroups,
  updateRoadmapSegment,
} from "../api/roadmapClient";
import type {
  RoadmapAnalysisFields,
  RoadmapCollaborationGroup,
  RoadmapCollaborationProvisioningResponse,
  RoadmapCollaborationRuntimeVerification,
  RoadmapLinkSummary,
  RoadmapProcessRecord,
  RoadmapProcessUseCaseLink,
  RoadmapProject,
  RoadmapQualityAuditReport,
  RoadmapMigrationsRequiredDetails,
  RoadmapSegmentRecord,
  RoadmapSynthesis,
  RoadmapUseCaseRecord,
} from "../api/roadmapClient";

import { InlineAlert } from "./InlineAlert";
import { EmptyState } from "./roadmap/DesignSystem";
import { AtlasMigrationsRequiredCallout } from "./roadmap/AtlasMigrationsRequiredCallout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui";

type ImportedRow = Record<string, unknown>;
type CustomerListItem = { id: string | number; name?: string };

const PROCESS_FIELD_CANDIDATES = ["ri-7", "ri7", "process", "processus"];
const USE_CASE_FIELD_CANDIDATES = ["ri-8", "ri8", "use case", "usecase", "cas d'usage", "cas d’usage"];
const USE_CASE_PROCESS_FIELD_CANDIDATES = ["ri-7", "ri7", "process", "processus", "process id", "processus id"];
const AIRTABLE_RECORD_ID_FIELD = "__airtableRecordId";
const EMPTY_SEGMENT_FORM = {
  name: "",
  purpose: "",
  solutionType: "",
  aiWhatItDoes: "",
  impact: "",
};

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase();
}

function detectField(headers: string[], candidates: string[]): string | "" {
  const normalized = headers.map((header) => ({ header, normalized: normalizeHeader(header) }));
  const match = normalized.find(({ normalized }) =>
    candidates.some((candidate) => normalized.includes(candidate)),
  );
  return match?.header ?? "";
}

function normalizeCell(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeCell(entry)).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value).trim();
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === "\"") {
      if (inQuotes && line[index + 1] === "\"") {
        current += "\"";
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  cells.push(current);
  return cells.map((cell) => cell.trim());
}

function parseCsv(contents: string): { headers: string[]; rows: ImportedRow[] } {
  const lines = contents
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return { headers: [], rows: [] };

  const headers = parseCsvLine(lines[0]).map((header, index) => header || `Column ${index + 1}`);
  const rows = lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    return headers.reduce<ImportedRow>((acc, header, index) => {
      acc[header] = cells[index] ?? "";
      return acc;
    }, {});
  });

  return { headers, rows };
}

function buildHeadersFromRows(sourceRows: ImportedRow[]): string[] {
  return Array.from(
    sourceRows.reduce((set, row) => {
      Object.keys(row).forEach((key) => set.add(key));
      return set;
    }, new Set<string>()),
  ).filter((header) => header !== AIRTABLE_RECORD_ID_FIELD);
}

function stripInternalFields(row: ImportedRow): ImportedRow {
  if (!(AIRTABLE_RECORD_ID_FIELD in row)) return row;
  const { [AIRTABLE_RECORD_ID_FIELD]: _ignored, ...rest } = row;
  return rest;
}

function isAirtableRecordId(value: unknown): boolean {
  return typeof value === "string" && value.startsWith("rec");
}

function isLinkedRecordField(rows: ImportedRow[], field: string): boolean {
  for (const row of rows) {
    const value = row[field];
    if (value == null || value === "") continue;
    return Array.isArray(value) && value.every((entry) => isAirtableRecordId(entry));
  }
  return false;
}

function selectionFromRecord(
  record: Record<string, boolean> | undefined,
  defaults: Array<{ key: string }>,
): string[] {
  const defaultKeys = defaults.map((field) => field.key);
  const selected = new Set<string>();
  defaultKeys.forEach((key) => {
    const value = record?.[key];
    if (value === false) return;
    selected.add(key);
  });
  Object.entries(record ?? {}).forEach(([key, value]) => {
    if (value) selected.add(key);
  });
  return Array.from(selected);
}

function recordFromSelection(selected: string[], defaults: Array<{ key: string }>): Record<string, boolean> {
  const record: Record<string, boolean> = {};
  defaults.forEach((field) => {
    record[field.key] = selected.includes(field.key);
  });
  selected.forEach((key) => {
    if (!(key in record)) {
      record[key] = true;
    }
  });
  return record;
}

function downloadJsonFile(filename: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

function parseNativeList(value: string): string[] {
  return value
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function parseEmailList(value: string): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];
  parseNativeList(value).forEach((entry) => {
    const email = entry.toLowerCase();
    if (!email || seen.has(email)) return;
    seen.add(email);
    emails.push(email);
  });
  return emails;
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim().toLowerCase());
}

function buildGroupId(value: string): string {
  const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return slug || "group";
}

const PROCESS_AI_FIELDS = [
  { key: "name", label: "Nom" },
  { key: "purpose", label: "Purpose" },
  { key: "steps", label: "Étapes" },
  { key: "risks", label: "Risques" },
  { key: "systems", label: "Systèmes" },
  { key: "reviewStatus", label: "Review status" },
  { key: "reviewOwner", label: "Process owner" },
];

const USE_CASE_AI_FIELDS = [
  { key: "name", label: "Nom" },
  { key: "problem", label: "Problème" },
  { key: "outcome", label: "Outcome" },
  { key: "aiOutputs", label: "AI outputs" },
  { key: "humanValidation", label: "Human validation" },
  { key: "acceptanceCriteria", label: "Acceptance criteria" },
  { key: "notes", label: "Notes" },
  { key: "opportunityScore", label: "Opportunity score" },
  { key: "impact", label: "Impact" },
  { key: "feasibility", label: "Feasibility" },
  { key: "risk", label: "Risk" },
  { key: "reviewStatus", label: "Review status" },
  { key: "reviewNotes", label: "Review notes" },
  { key: "processIds", label: "Process links" },
];

export function AiRoadmapProjectScreen({ hideHeader = false }: { hideHeader?: boolean }): JSX.Element {
  const location = useLocation();
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ImportedRow[]>([]);
  const [useCaseHeaders, setUseCaseHeaders] = useState<string[]>([]);
  const [useCaseRows, setUseCaseRows] = useState<ImportedRow[]>([]);
  const [processLabelField, setProcessLabelField] = useState<string>("");
  const [processMatchField, setProcessMatchField] = useState<string>("");
  const [useCaseField, setUseCaseField] = useState<string>("");
  const [useCaseProcessField, setUseCaseProcessField] = useState<string>("");
  const [importError, setImportError] = useState<string | null>(null);
  const [airtableBaseId, setAirtableBaseId] = useState("");
  const [airtableProcessesTable, setAirtableProcessesTable] = useState("");
  const [airtableProcessesView, setAirtableProcessesView] = useState("");
  const [airtableUseCasesTable, setAirtableUseCasesTable] = useState("");
  const [airtableUseCasesView, setAirtableUseCasesView] = useState("");
  const [airtableUsersTable, setAirtableUsersTable] = useState("");
  const [airtableUsersView, setAirtableUsersView] = useState("");
  const [airtableAppsTable, setAirtableAppsTable] = useState("");
  const [airtableAppsView, setAirtableAppsView] = useState("");
  const [airtableIntegrationsTable, setAirtableIntegrationsTable] = useState("");
  const [airtableIntegrationsView, setAirtableIntegrationsView] = useState("");
  const [airtableTechnicalAssessmentsTable, setAirtableTechnicalAssessmentsTable] = useState("");
  const [airtableTechnicalAssessmentsView, setAirtableTechnicalAssessmentsView] = useState("");
  const [airtableDataRiskChecklistsTable, setAirtableDataRiskChecklistsTable] = useState("");
  const [airtableDataRiskChecklistsView, setAirtableDataRiskChecklistsView] = useState("");
  const [hasAirtableToken, setHasAirtableToken] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeCustomer") ?? "";
  });
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeProject") ?? "";
  });
  const [newProjectName, setNewProjectName] = useState<string>("");
  const [loadedProcesses, setLoadedProcesses] = useState<RoadmapProcessRecord[]>([]);
  const [loadedUseCases, setLoadedUseCases] = useState<RoadmapUseCaseRecord[]>([]);
  const [loadedLinks, setLoadedLinks] = useState<RoadmapProcessUseCaseLink[]>([]);
  const [loadedCollaborationGroups, setLoadedCollaborationGroups] = useState<RoadmapCollaborationGroup[]>([]);
  const [linkSummary, setLinkSummary] = useState<RoadmapLinkSummary | null>(null);
  const [isSuggestingMapping, setIsSuggestingMapping] = useState(false);
  const [isApplyingMapping, setIsApplyingMapping] = useState(false);
  const [activeTab, setActiveTab] = useState<"native" | "contributors" | "import" | "aiFields">("native");
  const [nativeProcessName, setNativeProcessName] = useState("");
  const [nativeProcessDescription, setNativeProcessDescription] = useState("");
  const [nativePainPoints, setNativePainPoints] = useState("");
  const [nativeKpis, setNativeKpis] = useState("");
  const [nativeOwner, setNativeOwner] = useState("");
  const [nativeTools, setNativeTools] = useState("");
  const [nativeUseCaseTitle, setNativeUseCaseTitle] = useState("");
  const [nativeUseCaseOutcome, setNativeUseCaseOutcome] = useState("");
  const [nativeIdeaProblem, setNativeIdeaProblem] = useState("");
  const [nativeIdeaTarget, setNativeIdeaTarget] = useState("");
  const [nativeIdeaUrgency, setNativeIdeaUrgency] = useState("high");
  const [nativeIdeaComplexity, setNativeIdeaComplexity] = useState("medium");
  const [nativeIdeaValue, setNativeIdeaValue] = useState("strategic");
  const [nativeIdeaDependencies, setNativeIdeaDependencies] = useState("");
  const [nativeIntakeSaving, setNativeIntakeSaving] = useState(false);
  const [nativeIntakeNotice, setNativeIntakeNotice] = useState<string | null>(null);
  const [collaborationGroupName, setCollaborationGroupName] = useState("");
  const [collaborationLeadEmail, setCollaborationLeadEmail] = useState("");
  const [collaborationMemberEmails, setCollaborationMemberEmails] = useState("");
  const [selectedCollaborationGroupId, setSelectedCollaborationGroupId] = useState("");
  const [collaborationContributorEmail, setCollaborationContributorEmail] = useState("");
  const [collaborationSaving, setCollaborationSaving] = useState(false);
  const [collaborationNotice, setCollaborationNotice] = useState<string | null>(null);
  const [collaborationSendEmail, setCollaborationSendEmail] = useState(false);
  const [collaborationProvisioning, setCollaborationProvisioning] = useState(false);
  const [collaborationProvisioningResult, setCollaborationProvisioningResult] =
    useState<RoadmapCollaborationProvisioningResponse | null>(null);
  const [collaborationRuntimeLoading, setCollaborationRuntimeLoading] = useState(false);
  const [collaborationRuntime, setCollaborationRuntime] = useState<RoadmapCollaborationRuntimeVerification | null>(null);
  const [analysisProcessFields, setAnalysisProcessFields] = useState<string[]>([]);
  const [analysisUseCaseFields, setAnalysisUseCaseFields] = useState<string[]>([]);
  const [analysisProcessRawFields, setAnalysisProcessRawFields] = useState<string[]>([]);
  const [analysisUseCaseRawFields, setAnalysisUseCaseRawFields] = useState<string[]>([]);
  const [analysisIncludeRawFields, setAnalysisIncludeRawFields] = useState(true);
  const [analysisFieldsError, setAnalysisFieldsError] = useState<string | null>(null);
  const [analysisFieldsReady, setAnalysisFieldsReady] = useState(false);
  const [analysisFieldsSaving, setAnalysisFieldsSaving] = useState(false);
  const [analysisFieldsMigration, setAnalysisFieldsMigration] =
    useState<RoadmapMigrationsRequiredDetails | null>(null);
  const [segments, setSegments] = useState<RoadmapSegmentRecord[]>([]);
  const [segmentError, setSegmentError] = useState<string | null>(null);
  const [isSegmentsLoading, setIsSegmentsLoading] = useState(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>("");
  const [segmentForm, setSegmentForm] = useState(EMPTY_SEGMENT_FORM);
  const [newSegmentForm, setNewSegmentForm] = useState(EMPTY_SEGMENT_FORM);
  const [qualityAudit, setQualityAudit] = useState<RoadmapQualityAuditReport | null>(null);
  const [qualityAuditError, setQualityAuditError] = useState<string | null>(null);
  const [isQualityAuditLoading, setIsQualityAuditLoading] = useState(false);
  const [qualityAuditMigration, setQualityAuditMigration] =
    useState<RoadmapMigrationsRequiredDetails | null>(null);
  const [auditSeverityFilter, setAuditSeverityFilter] = useState("ALL");
  const [auditEntityFilter, setAuditEntityFilter] = useState("ALL");
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [synthesis, setSynthesis] = useState<RoadmapSynthesis | null>(null);
  const [synthesisError, setSynthesisError] = useState<string | null>(null);
  const [isSynthesisLoading, setIsSynthesisLoading] = useState(false);
  const [synthesisMigration, setSynthesisMigration] = useState<RoadmapMigrationsRequiredDetails | null>(null);

  const { data: customerOptionsData } = useEntityList<CustomerListItem>({
    entity: "Customer",
    page: 0,
    pageSize: 1000,
  });

  const customerOptions = useMemo(() => {
    const options = (customerOptionsData ?? []).map((customer) => ({
      value: String(customer.id),
      label: customer.name ?? String(customer.id),
    }));
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [customerOptionsData]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );
  const canOverrideQualityGate = selectedProject?.role === "ADMIN";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "native" || tab === "contributors" || tab === "import" || tab === "aiFields") {
      setActiveTab(tab);
    }
    const segmentId = params.get("segmentId");
    if (segmentId) {
      setSelectedSegmentId(segmentId);
    }
  }, [location.search]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setProjects([]);
      setSelectedProjectId("");
      localStorage.removeItem("studio.aiRoadmap.activeCustomer");
      return;
    }
    localStorage.setItem("studio.aiRoadmap.activeCustomer", selectedCustomerId);
    let isActive = true;
    fetchRoadmapProjects(selectedCustomerId)
      .then((loaded) => {
        if (!isActive) return;
        setProjects(loaded);
        if (loaded.length && !loaded.find((project) => project.id === selectedProjectId)) {
          setSelectedProjectId(loaded[0].id);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setImportError("Impossible de charger les projets de roadmap.");
      });
    return () => {
      isActive = false;
    };
  }, [selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    if (!selectedCustomerId || !selectedProjectId) return;
    localStorage.setItem("studio.aiRoadmap.activeProject", selectedProjectId);
    window.dispatchEvent(new Event("roadmap:project-changed"));
    let isActive = true;
    fetchRoadmapSettings(selectedProjectId)
      .then((settings) => {
        if (!isActive) return;
        if (!settings) {
          setAirtableBaseId("");
          setHasAirtableToken(false);
          setAirtableProcessesTable("");
          setAirtableProcessesView("");
          setAirtableUseCasesTable("");
          setAirtableUseCasesView("");
          setAirtableUsersTable("");
          setAirtableUsersView("");
          setAirtableAppsTable("");
          setAirtableAppsView("");
          setAirtableIntegrationsTable("");
          setAirtableIntegrationsView("");
          setAirtableTechnicalAssessmentsTable("");
          setAirtableTechnicalAssessmentsView("");
          setAirtableDataRiskChecklistsTable("");
          setAirtableDataRiskChecklistsView("");
          return;
        }
        setAirtableBaseId(settings.baseId ?? "");
        setHasAirtableToken(Boolean(settings.hasToken));
        setAirtableProcessesTable(settings.processesTable ?? "");
        setAirtableProcessesView(settings.processesView ?? "");
        setAirtableUseCasesTable(settings.useCasesTable ?? "");
        setAirtableUseCasesView(settings.useCasesView ?? "");
        setAirtableUsersTable(settings.usersTable ?? "");
        setAirtableUsersView(settings.usersView ?? "");
        setAirtableAppsTable(settings.appsTable ?? "");
        setAirtableAppsView(settings.appsView ?? "");
        setAirtableIntegrationsTable(settings.integrationsTable ?? "");
        setAirtableIntegrationsView(settings.integrationsView ?? "");
        setAirtableTechnicalAssessmentsTable(settings.technicalAssessmentsTable ?? "");
        setAirtableTechnicalAssessmentsView(settings.technicalAssessmentsView ?? "");
        setAirtableDataRiskChecklistsTable(settings.dataRiskChecklistsTable ?? "");
        setAirtableDataRiskChecklistsView(settings.dataRiskChecklistsView ?? "");
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setImportError("Impossible de charger les paramètres Airtable.");
      });
    return () => {
      isActive = false;
    };
  }, [selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setLoadedProcesses([]);
      setLoadedUseCases([]);
      setLoadedLinks([]);
      setLoadedCollaborationGroups([]);
      setSelectedCollaborationGroupId("");
      setCollaborationProvisioningResult(null);
      setCollaborationRuntime(null);
      setCollaborationSendEmail(false);
      setAnalysisProcessFields([]);
      setAnalysisUseCaseFields([]);
      setAnalysisProcessRawFields([]);
      setAnalysisUseCaseRawFields([]);
      setAnalysisIncludeRawFields(true);
      setAnalysisFieldsError(null);
      setAnalysisFieldsReady(false);
      setAnalysisFieldsSaving(false);
      setAnalysisFieldsMigration(null);
      setQualityAudit(null);
      setQualityAuditError(null);
      setQualityAuditMigration(null);
      setSynthesis(null);
      setSynthesisError(null);
      setSynthesisMigration(null);
      return;
    }
    let isActive = true;
    Promise.all([
      fetchRoadmapData(selectedProjectId),
      fetchRoadmapCollaborationGroups(selectedProjectId).catch((error) => {
        console.error(error);
        return [] as RoadmapCollaborationGroup[];
      }),
    ])
      .then(([data, groups]) => {
        if (!isActive) return;
        setLoadedProcesses(data.processes);
        setLoadedUseCases(data.useCases);
        setLoadedLinks(data.links ?? []);
        setLoadedCollaborationGroups(groups);
        setCollaborationProvisioningResult(null);
        setCollaborationRuntime(null);
        setSelectedCollaborationGroupId((current) =>
          current && groups.some((group) => group.id === current) ? current : groups[0]?.id ?? "",
        );
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setImportError("Impossible de charger les données de roadmap.");
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;
    let isActive = true;
    setIsQualityAuditLoading(true);
    setQualityAuditMigration(null);
    fetchRoadmapQualityAudit(selectedProjectId)
      .then((report) => {
        if (!isActive) return;
        setQualityAudit(report);
        setQualityAuditError(null);
      })
      .catch((error) => {
        if (!isActive) return;
        if (isMigrationsRequiredError(error)) {
          setQualityAuditMigration(error.details);
          setQualityAuditError(null);
          setQualityAudit(null);
          return;
        }
        console.error(error);
        setQualityAuditError("Impossible de charger l'audit qualité.");
      })
      .finally(() => {
        if (!isActive) return;
        setIsQualityAuditLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;
    let isActive = true;
    setIsSynthesisLoading(true);
    setSynthesisMigration(null);
    fetchRoadmapSynthesis(selectedProjectId)
      .then((report) => {
        if (!isActive) return;
        setSynthesis(report);
        setSynthesisError(null);
      })
      .catch((error) => {
        if (!isActive) return;
        if (isMigrationsRequiredError(error)) {
          setSynthesisMigration(error.details);
          setSynthesisError(null);
          setSynthesis(null);
          return;
        }
        console.error(error);
        setSynthesisError("Impossible de charger la synthèse IA.");
      })
      .finally(() => {
        if (!isActive) return;
        setIsSynthesisLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;
    let isActive = true;
    setAnalysisFieldsReady(false);
    setAnalysisFieldsMigration(null);
    fetchRoadmapAnalysisFields(selectedProjectId)
      .then((fields) => {
        if (!isActive) return;
        setAnalysisProcessFields(selectionFromRecord(fields.processFields, PROCESS_AI_FIELDS));
        setAnalysisUseCaseFields(selectionFromRecord(fields.useCaseFields, USE_CASE_AI_FIELDS));
        setAnalysisProcessRawFields(fields.processRawFields ?? []);
        setAnalysisUseCaseRawFields(fields.useCaseRawFields ?? []);
        setAnalysisIncludeRawFields(fields.includeRawFields !== false);
        setAnalysisFieldsError(null);
        setAnalysisFieldsReady(true);
      })
      .catch((error) => {
        if (!isActive) return;
        if (isMigrationsRequiredError(error)) {
          setAnalysisFieldsMigration(error.details);
          setAnalysisFieldsError(null);
          setAnalysisFieldsReady(true);
          return;
        }
        console.error(error);
        setAnalysisProcessFields(PROCESS_AI_FIELDS.map((field) => field.key));
        setAnalysisUseCaseFields(USE_CASE_AI_FIELDS.map((field) => field.key));
        setAnalysisProcessRawFields([]);
        setAnalysisUseCaseRawFields([]);
        setAnalysisIncludeRawFields(true);
        setAnalysisFieldsError("Impossible de charger les champs IA.");
        setAnalysisFieldsReady(true);
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setSegments([]);
      setSelectedSegmentId("");
      setSegmentError(null);
      return;
    }
    let isActive = true;
    setIsSegmentsLoading(true);
    fetchRoadmapSegments(selectedProjectId)
      .then((loaded) => {
        if (!isActive) return;
        setSegments(loaded);
        setSelectedSegmentId((current) => {
          if (current && loaded.some((segment) => segment.id === current)) {
            return current;
          }
          return loaded[0]?.id ?? "";
        });
        setSegmentError(null);
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setSegmentError("Impossible de charger les applications.");
      })
      .finally(() => {
        if (isActive) setIsSegmentsLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    const selectedSegment = segments.find((segment) => segment.id === selectedSegmentId);
    if (!selectedSegment) {
      setSegmentForm(EMPTY_SEGMENT_FORM);
      return;
    }
    setSegmentForm({
      name: selectedSegment.name ?? "",
      purpose: selectedSegment.purpose ?? "",
      solutionType: selectedSegment.solutionType ?? "",
      aiWhatItDoes: selectedSegment.aiWhatItDoes ?? "",
      impact: selectedSegment.impact ?? "",
    });
  }, [segments, selectedSegmentId]);

  useEffect(() => {
    if (!selectedProjectId) {
      localStorage.removeItem("studio.aiRoadmap.activeProject");
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId || !loadedProcesses.length || !loadedUseCases.length) return;
    if (processLabelField || processMatchField || useCaseField || useCaseProcessField) return;
    setIsSuggestingMapping(true);
    fetchRoadmapMappingSuggestion(selectedProjectId)
      .then((suggestion) => {
        setProcessLabelField(suggestion.processLabelField);
        const hasAirtableIds = loadedProcesses.some((process) => process.airtableRecordId);
        setProcessMatchField(
          hasAirtableIds ? AIRTABLE_RECORD_ID_FIELD : suggestion.processMatchField || suggestion.processLabelField,
        );
        setUseCaseField(suggestion.useCaseField);
        setUseCaseProcessField(suggestion.useCaseProcessField);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setIsSuggestingMapping(false));
  }, [
    loadedProcesses.length,
    loadedUseCases.length,
    processLabelField,
    processMatchField,
    selectedProjectId,
    useCaseField,
    useCaseProcessField,
  ]);

  useEffect(() => {
    if (!rows.length && loadedProcesses.length) {
      const normalized = loadedProcesses
        .map((process) => ({
          ...(process.raw ?? {}),
          ...(process.airtableRecordId ? { [AIRTABLE_RECORD_ID_FIELD]: process.airtableRecordId } : {}),
        }))
        .filter((row) => Object.keys(row).length);
      if (normalized.length) {
        setRows(normalized);
        setHeaders(buildHeadersFromRows(normalized));
      }
    }
  }, [loadedProcesses, rows.length]);

  useEffect(() => {
    if (!useCaseRows.length && loadedUseCases.length) {
      const normalized = loadedUseCases
        .map((useCase) => ({
          ...(useCase.raw ?? {}),
          ...(useCase.airtableRecordId ? { [AIRTABLE_RECORD_ID_FIELD]: useCase.airtableRecordId } : {}),
        }))
        .filter((row) => Object.keys(row).length);
      if (normalized.length) {
        setUseCaseRows(normalized);
        setUseCaseHeaders(buildHeadersFromRows(normalized));
      }
    }
  }, [loadedUseCases, useCaseRows.length]);

  const effectiveUseCaseRows = useCaseRows.length ? useCaseRows : rows;

  const missingMapping =
    (headers.length > 0 || useCaseHeaders.length > 0) &&
    (!processLabelField || !processMatchField || !useCaseField || !useCaseProcessField);
  const hasAirtableRecordIds = useMemo(
    () =>
      rows.some((row) => Boolean(row[AIRTABLE_RECORD_ID_FIELD])) ||
      loadedProcesses.some((process) => process.airtableRecordId),
    [loadedProcesses, rows],
  );
  const invalidProcessMatchField =
    processMatchField &&
    processMatchField !== AIRTABLE_RECORD_ID_FIELD &&
    isLinkedRecordField(rows, processMatchField);
  const invalidUseCaseProcessField =
    hasAirtableRecordIds && useCaseProcessField && !isLinkedRecordField(effectiveUseCaseRows, useCaseProcessField);
  const mappingValidationError = invalidProcessMatchField
    ? "La clé de matching Processus ne doit pas être un champ de liens Airtable."
    : invalidUseCaseProcessField
      ? "Le champ Processus côté Use Cases doit être un lien Airtable (liste d'IDs Airtable)."
      : null;

  const selectedCollaborationGroup = useMemo(
    () => loadedCollaborationGroups.find((group) => group.id === selectedCollaborationGroupId) ?? null,
    [loadedCollaborationGroups, selectedCollaborationGroupId],
  );

  const useCaseSegmentMap = useMemo(() => {
    const map = new Map<string, string>();
    segments.forEach((segment) => {
      (segment.useCaseIds ?? []).forEach((useCaseId) => {
        map.set(useCaseId, segment.id);
      });
    });
    return map;
  }, [segments]);

  const sortedUseCases = useMemo(() => {
    return [...loadedUseCases].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [loadedUseCases]);

  const processMatchOptions = useMemo(
    () => headers.filter((header) => !isLinkedRecordField(rows, header)),
    [headers, rows],
  );
  const useCaseFieldOptions = useMemo(
    () => (useCaseHeaders.length ? useCaseHeaders : headers),
    [headers, useCaseHeaders],
  );
  const processRawFieldOptions = useMemo(() => {
    const keys = new Set<string>();
    loadedProcesses.forEach((process) => {
      const raw = process.raw ?? {};
      if (raw && typeof raw === "object") {
        Object.keys(raw).forEach((key) => keys.add(key));
      }
    });
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }, [loadedProcesses]);
  const useCaseRawFieldOptions = useMemo(() => {
    const keys = new Set<string>();
    loadedUseCases.forEach((useCase) => {
      const raw = useCase.raw ?? {};
      if (raw && typeof raw === "object") {
        Object.keys(raw).forEach((key) => keys.add(key));
      }
    });
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }, [loadedUseCases]);

  const useCaseToProcessId = useMemo(() => {
    const map = new Map<string, string>();
    loadedLinks.forEach((link) => {
      if (!map.has(link.useCaseId)) {
        map.set(link.useCaseId, link.processId);
      }
    });
    return map;
  }, [loadedLinks]);

  const filteredAuditIssues = useMemo(() => {
    if (!qualityAudit?.issues?.length) return [];
    return qualityAudit.issues.filter((issue) => {
      if (auditSeverityFilter !== "ALL" && issue.severity !== auditSeverityFilter) return false;
      if (auditEntityFilter !== "ALL" && issue.entityType !== auditEntityFilter) return false;
      return true;
    });
  }, [auditEntityFilter, auditSeverityFilter, qualityAudit]);

  useEffect(() => {
    if (!selectedProjectId || !analysisFieldsReady) return;
    const payload: RoadmapAnalysisFields = {
      processFields: recordFromSelection(analysisProcessFields, PROCESS_AI_FIELDS),
      useCaseFields: recordFromSelection(analysisUseCaseFields, USE_CASE_AI_FIELDS),
      processRawFields: analysisProcessRawFields,
      useCaseRawFields: analysisUseCaseRawFields,
      includeRawFields: analysisIncludeRawFields,
    };
    const timeout = window.setTimeout(() => {
      setAnalysisFieldsSaving(true);
      saveRoadmapAnalysisFields(selectedProjectId, payload)
        .then(() => {
          setAnalysisFieldsError(null);
          setAnalysisFieldsMigration(null);
        })
        .catch((error) => {
          if (isMigrationsRequiredError(error)) {
            setAnalysisFieldsMigration(error.details);
            setAnalysisFieldsError(null);
            return;
          }
          console.error(error);
          setAnalysisFieldsError("Impossible d'enregistrer les champs IA.");
        })
        .finally(() => {
          setAnalysisFieldsSaving(false);
        });
    }, 400);
    return () => window.clearTimeout(timeout);
  }, [
    analysisIncludeRawFields,
    analysisProcessFields,
    analysisProcessRawFields,
    analysisUseCaseFields,
    analysisUseCaseRawFields,
    analysisFieldsReady,
    selectedProjectId,
  ]);

  const toggleSelection = (value: string, current: string[], setter: (next: string[]) => void) => {
    if (current.includes(value)) {
      setter(current.filter((entry) => entry !== value));
      return;
    }
    setter([...current, value]);
  };

  const stopReasonBadgeClass = (stopReason?: string | null) => {
    switch (stopReason) {
      case "PASS":
        return "bg-emerald-100 text-emerald-800";
      case "FAIL":
        return "bg-rose-100 text-rose-800";
      case "NO_OP":
        return "bg-slate-100 text-slate-700";
      case "NEEDS_APPROVAL":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const handleRunQualityAudit = async () => {
    if (!selectedProjectId) return;
    setIsQualityAuditLoading(true);
    setQualityAuditError(null);
    setQualityAuditMigration(null);
    try {
      const report = await runRoadmapQualityAudit(selectedProjectId);
      setQualityAudit(report);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setQualityAuditMigration(error.details);
        setQualityAuditError(null);
        return;
      }
      console.error(error);
      setQualityAuditError("Impossible de lancer l'audit qualité.");
    } finally {
      setIsQualityAuditLoading(false);
    }
  };

  const handleOverrideQualityGate = async () => {
    if (!selectedProjectId) return;
    setOverrideLoading(true);
    setOverrideError(null);
    try {
      await overrideRoadmapQualityAudit(selectedProjectId, overrideReason.trim());
      const refreshed = await fetchRoadmapQualityAudit(selectedProjectId);
      setQualityAudit(refreshed);
      setShowOverrideDialog(false);
      setOverrideReason("");
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : "Impossible d'appliquer la dérogation.";
      setOverrideError(message);
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleRunSynthesis = async () => {
    if (!selectedProjectId) return;
    setIsSynthesisLoading(true);
    setSynthesisError(null);
    setSynthesisMigration(null);
    try {
      const report = await runRoadmapSynthesis(selectedProjectId);
      setSynthesis(report);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setSynthesisMigration(error.details);
        setSynthesisError(null);
        return;
      }
      console.error(error);
      setSynthesisError("Impossible de lancer la synthèse IA.");
    } finally {
      setIsSynthesisLoading(false);
    }
  };

  const handleDownloadAudit = () => {
    if (!qualityAudit) return;
    downloadJsonFile(`atlas-quality-audit-${selectedProjectId ?? "project"}.json`, qualityAudit);
  };

  const handleDownloadSynthesis = () => {
    if (!synthesis) return;
    downloadJsonFile(`atlas-synthesis-${selectedProjectId ?? "project"}.json`, synthesis);
  };

  const handleApplyStandardFields = () => {
    setAnalysisProcessFields(PROCESS_AI_FIELDS.map((field) => field.key));
    setAnalysisUseCaseFields(USE_CASE_AI_FIELDS.map((field) => field.key));
    setAnalysisProcessRawFields([]);
    setAnalysisUseCaseRawFields([]);
    setAnalysisIncludeRawFields(true);
  };

  function loadImportedData(nextHeaders: string[], nextRows: ImportedRow[]) {
    setHeaders(nextHeaders);
    setRows(nextRows);
    const detectedProcess = detectField(nextHeaders, PROCESS_FIELD_CANDIDATES);
    const detectedUseCase = detectField(nextHeaders, USE_CASE_FIELD_CANDIDATES);
    const detectedUseCaseProcess = detectField(nextHeaders, USE_CASE_PROCESS_FIELD_CANDIDATES);
    setProcessLabelField(detectedProcess);
    setProcessMatchField(
      nextRows.some((row) => row[AIRTABLE_RECORD_ID_FIELD]) ? AIRTABLE_RECORD_ID_FIELD : detectedProcess,
    );
    setUseCaseField(detectedUseCase);
    setUseCaseProcessField(detectedUseCaseProcess || detectedProcess);
    setImportError(null);
    setLinkSummary(null);
  }

  function loadUseCaseData(nextHeaders: string[], nextRows: ImportedRow[]) {
    setUseCaseHeaders(nextHeaders);
    setUseCaseRows(nextRows);
    const detectedUseCase = detectField(nextHeaders, USE_CASE_FIELD_CANDIDATES);
    const detectedUseCaseProcess = detectField(nextHeaders, USE_CASE_PROCESS_FIELD_CANDIDATES);
    setUseCaseField(detectedUseCase);
    setUseCaseProcessField(detectedUseCaseProcess || processLabelField);
    setImportError(null);
    setLinkSummary(null);
  }

  async function handleCsvUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (!parsed.headers.length) {
        setImportError("Le fichier CSV est vide ou non lisible.");
        return;
      }
      loadImportedData(parsed.headers, parsed.rows);
    } catch (error) {
      console.error(error);
      setImportError("Impossible de lire le fichier CSV.");
    } finally {
      event.target.value = "";
    }
  }

  async function handleUseCasesCsvUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (!parsed.headers.length) {
        setImportError("Le fichier CSV des use cases est vide ou non lisible.");
        return;
      }
      loadUseCaseData(parsed.headers, parsed.rows);
    } catch (error) {
      console.error(error);
      setImportError("Impossible de lire le fichier CSV des use cases.");
    } finally {
      event.target.value = "";
    }
  }

  async function persistRoadmapData(
    nextRows = rows,
    nextUseCaseRows = effectiveUseCaseRows,
    optionalPayload?: {
      users?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
      apps?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
      integrations?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
      technicalAssessments?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
      dataRiskChecklists?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
    },
  ) {
    if (!selectedProjectId) return;
    const processesPayload = nextRows.map((row) => ({
      name: normalizeCell(row[processLabelField]),
      matchKey:
        processMatchField === AIRTABLE_RECORD_ID_FIELD
          ? normalizeCell(row[AIRTABLE_RECORD_ID_FIELD])
          : normalizeCell(row[processMatchField] ?? row[processLabelField]),
      airtableRecordId: normalizeCell(row[AIRTABLE_RECORD_ID_FIELD]) || undefined,
      raw: stripInternalFields(row),
    }));
    const useCasesPayload = nextUseCaseRows.map((row) => ({
      name: normalizeCell(row[useCaseField]),
      linkedProcessKey: normalizeCell(row[useCaseProcessField]),
      airtableRecordId: normalizeCell(row[AIRTABLE_RECORD_ID_FIELD]) || undefined,
      raw: stripInternalFields(row),
    }));
    const importUsersPayload = optionalPayload?.users ?? [];
    const importAppsPayload = optionalPayload?.apps ?? [];
    const importIntegrationsPayload = optionalPayload?.integrations ?? [];
    const importTechnicalAssessmentsPayload = optionalPayload?.technicalAssessments ?? [];
    const importDataRiskPayload = optionalPayload?.dataRiskChecklists ?? [];
    const importChunkMaxBytes = 180000;
    const importChunkMaxRows = 40;
    const estimatePayloadBytes = (value: unknown): number => {
      if (value === undefined || value === null) return 0;
      try {
        return JSON.stringify(value).length;
      } catch {
        return 1_000;
      }
    };
    const toChunks = <T,>(items: T[]): T[][] => {
      if (!items.length) return [];
      const chunks: T[][] = [];
      const budget = Math.max(1, importChunkMaxBytes);
      const maxRows = Math.max(1, importChunkMaxRows);
      let currentRows: T[] = [];
      let currentBytes = 0;
      for (const item of items) {
        const itemBytes = estimatePayloadBytes(item);
        if (currentRows.length >= maxRows) {
          chunks.push(currentRows);
          currentRows = [];
          currentBytes = 0;
        }
        const separatorBytes = currentRows.length > 0 ? 1 : 0;
        const projectedBytes = currentBytes + separatorBytes + itemBytes;
        if (currentRows.length > 0 && projectedBytes > budget) {
          chunks.push(currentRows);
          currentRows = [];
          currentBytes = 0;
        }
        currentRows.push(item);
        currentBytes += (currentRows.length > 1 ? 1 : 0) + itemBytes;
      }
      if (currentRows.length) chunks.push(currentRows);
      return chunks;
    };
    const processChunks = toChunks(processesPayload);
    const useCaseChunks = toChunks(useCasesPayload);
    const userChunks = toChunks(importUsersPayload);
    const appChunks = toChunks(importAppsPayload);
    const integrationChunks = toChunks(importIntegrationsPayload);
    const technicalAssessmentChunks = toChunks(importTechnicalAssessmentsPayload);
    const dataRiskChecklistsChunks = toChunks(importDataRiskPayload);
    const totalChunks = Math.max(
      processChunks.length,
      useCaseChunks.length,
      userChunks.length,
      appChunks.length,
      integrationChunks.length,
      technicalAssessmentChunks.length,
      dataRiskChecklistsChunks.length,
    );
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
      await saveRoadmapData(selectedProjectId, {
        processes: processChunks[chunkIndex] ?? [],
        useCases: useCaseChunks[chunkIndex] ?? [],
        users: userChunks[chunkIndex] ?? [],
        apps: appChunks[chunkIndex] ?? [],
        integrations: integrationChunks[chunkIndex] ?? [],
        technicalAssessments: technicalAssessmentChunks[chunkIndex] ?? [],
        dataRiskChecklists: dataRiskChecklistsChunks[chunkIndex] ?? [],
        replaceExisting: chunkIndex === 0,
      });
    }
    const refreshed = await fetchRoadmapData(selectedProjectId);
    setLoadedProcesses(refreshed.processes);
    setLoadedUseCases(refreshed.useCases);
  }

  async function handleApplyMapping() {
    if (!selectedProjectId) return;
    setIsApplyingMapping(true);
    try {
      const summary = await applyRoadmapMapping(selectedProjectId, {
        processLabelField,
        processMatchField,
        useCaseField,
        useCaseProcessField,
      });
      setLinkSummary(summary);
      const data = await fetchRoadmapData(selectedProjectId);
      setLoadedProcesses(data.processes);
      setLoadedUseCases(data.useCases);
    } catch (error) {
      console.error(error);
      setImportError(error instanceof Error ? error.message : "Impossible d'appliquer le mapping IA.");
    } finally {
      setIsApplyingMapping(false);
    }
  }

  async function handleAirtableImport() {
    setIsImporting(true);
    setImportError(null);
    try {
      if (!selectedCustomerId || !selectedProjectId) {
        setImportError("Sélectionnez un client et un projet de roadmap.");
        return;
      }
      if (!airtableBaseId || !airtableProcessesTable || !airtableUseCasesTable || !hasAirtableToken) {
        setImportError("Base ID, tables Process et Use Cases, et token Airtable sont requis.");
        return;
      }

      const {
        processRows,
        useCaseRows,
        userRows,
        appRows,
        integrationRows,
        technicalAssessmentRows,
        dataRiskChecklistRows,
      } = await importRoadmapFromAirtable(selectedProjectId);

      const nextHeaders = Array.from(
        processRows.reduce((set, row) => {
          Object.keys(row.fields ?? {}).forEach((key) => set.add(key));
          return set;
        }, new Set<string>()),
      );
      const nextUseCaseHeaders = Array.from(
        useCaseRows.reduce((set, row) => {
          Object.keys(row.fields ?? {}).forEach((key) => set.add(key));
          return set;
        }, new Set<string>()),
      );
      if (!nextHeaders.length) {
        setImportError("Aucune donnée Airtable trouvée pour les processus.");
        return;
      }
      if (!nextUseCaseHeaders.length) {
        setImportError("Aucune donnée Airtable trouvée pour les use cases.");
        return;
      }
      const normalizedProcessRows = processRows.map((row) => ({
        ...row.fields,
        [AIRTABLE_RECORD_ID_FIELD]: row.id,
      }));
      const normalizedUseCaseRows = useCaseRows.map((row) => ({
        ...row.fields,
        [AIRTABLE_RECORD_ID_FIELD]: row.id,
      }));
      loadImportedData(nextHeaders, normalizedProcessRows);
      loadUseCaseData(nextUseCaseHeaders, normalizedUseCaseRows);
      const buildOptionalPayload = (rows: { id: string; fields: Record<string, unknown> }[]) =>
        rows.map((row) => ({
          airtableRecordId: normalizeCell(row.id) || undefined,
          raw: stripInternalFields({ ...row.fields, [AIRTABLE_RECORD_ID_FIELD]: row.id }),
        }));
      await persistRoadmapData(normalizedProcessRows, normalizedUseCaseRows, {
        users: buildOptionalPayload(userRows),
        apps: buildOptionalPayload(appRows),
        integrations: buildOptionalPayload(integrationRows),
        technicalAssessments: buildOptionalPayload(technicalAssessmentRows),
        dataRiskChecklists: buildOptionalPayload(dataRiskChecklistRows),
      });
    } catch (error) {
      console.error(error);
      setImportError(
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : "Import Airtable impossible. Vérifiez les identifiants et droits d'accès.",
      );
    } finally {
      setIsImporting(false);
    }
  }

  function handleClearImport() {
    setHeaders([]);
    setRows([]);
    setUseCaseHeaders([]);
    setUseCaseRows([]);
    setProcessLabelField("");
    setProcessMatchField("");
    setUseCaseField("");
    setUseCaseProcessField("");
    setImportError(null);
    setLinkSummary(null);
  }

  async function handleAddProject() {
    if (!selectedCustomerId) {
      setImportError("Sélectionnez un client avant de créer un projet.");
      return;
    }
    if (!newProjectName.trim()) {
      setImportError("Le nom du projet est requis.");
      return;
    }
    try {
      const newProject = await createRoadmapProject(selectedCustomerId, newProjectName.trim());
      setProjects((current) => [newProject, ...current]);
      setSelectedProjectId(newProject.id);
      setNewProjectName("");
      setImportError(null);
    } catch (error) {
      console.error(error);
      setImportError("Impossible de créer le projet de roadmap.");
    }
  }

  async function handleSaveCollaborationGroup() {
    if (!selectedProjectId) {
      setCollaborationNotice("Selectionnez un projet Atlas avant de creer un groupe.");
      return;
    }
    const name = collaborationGroupName.trim();
    const leadEmail = collaborationLeadEmail.trim().toLowerCase();
    const memberEmails = parseEmailList(collaborationMemberEmails);
    if (!name || !leadEmail || !isEmail(leadEmail)) {
      setCollaborationNotice("Nom du groupe et e-mail responsable valide sont requis.");
      return;
    }
    if (!memberEmails.every(isEmail)) {
      setCollaborationNotice("La liste des membres contient au moins un e-mail invalide.");
      return;
    }
    const group: RoadmapCollaborationGroup = {
      id: buildGroupId(name),
      name,
      title: name,
      leadEmail,
      memberEmails: Array.from(new Set([leadEmail, ...memberEmails])),
      source: "atlas_group_collaboration_v1",
    };
    setCollaborationSaving(true);
    setCollaborationNotice(null);
    try {
      const nextGroups = [
        ...loadedCollaborationGroups.filter((current) => current.id !== group.id),
        group,
      ].sort((a, b) => a.name.localeCompare(b.name));
      const savedGroups = await saveRoadmapCollaborationGroups(selectedProjectId, nextGroups);
      setLoadedCollaborationGroups(savedGroups);
      setSelectedCollaborationGroupId(group.id);
      setCollaborationGroupName("");
      setCollaborationLeadEmail("");
      setCollaborationMemberEmails("");
      setCollaborationNotice("Groupe enregistre. Le responsable deviendra UC Owner des contributions du groupe.");
    } catch (error) {
      console.error(error);
      setCollaborationNotice("Enregistrement du groupe impossible. Verifiez les droits manageMembers et les migrations Atlas.");
    } finally {
      setCollaborationSaving(false);
    }
  }

  async function handleVerifyCollaborationRuntime(requireEmailDelivery = collaborationSendEmail) {
    if (!selectedProjectId) {
      setCollaborationNotice("Selectionnez un projet Atlas avant la verification runtime.");
      return null;
    }
    setCollaborationRuntimeLoading(true);
    setCollaborationNotice(null);
    try {
      const runtime = await fetchRoadmapCollaborationRuntimeVerification(selectedProjectId, requireEmailDelivery);
      setCollaborationRuntime(runtime);
      if (runtime.status === "blocked") {
        setCollaborationNotice("Verification runtime bloquee: corrigez les blockers avant de declarer un GO production.");
      } else if (runtime.status === "ready_with_warnings") {
        setCollaborationNotice("Runtime pret avec warnings: les comptes/projets existent, mais l'envoi e-mail reel n'est pas verifie.");
      } else {
        setCollaborationNotice("Runtime verifie: comptes, invitations et memberships sont coherents.");
      }
      return runtime;
    } catch (error) {
      console.error(error);
      setCollaborationNotice("Verification runtime impossible. Verifiez les droits manageMembers et les migrations Atlas.");
      return null;
    } finally {
      setCollaborationRuntimeLoading(false);
    }
  }

  async function handleProvisionCollaborationGroups() {
    if (!selectedProjectId) {
      setCollaborationNotice("Selectionnez un projet Atlas avant de provisionner les comptes.");
      return;
    }
    if (!loadedCollaborationGroups.length) {
      setCollaborationNotice("Creez au moins un groupe avant de provisionner les comptes.");
      return;
    }
    setCollaborationProvisioning(true);
    setCollaborationNotice(null);
    try {
      const result = await provisionRoadmapCollaborationGroups(selectedProjectId, {
        sendEmail: collaborationSendEmail,
        createAccounts: true,
        createInvites: true,
        reason: "Atlas group collaboration invitation",
      });
      setCollaborationProvisioningResult(result);
      const sent = result.summary.emailSent;
      const outbox = result.summary.outboxTokens;
      setCollaborationNotice(
        collaborationSendEmail
          ? `Provisioning termine: ${result.summary.createdAccounts} comptes crees, ${sent} e-mails envoyes, ${outbox} invitations en outbox.`
          : `Provisioning termine sans egress: ${result.summary.createdAccounts} comptes crees, ${outbox} invitations en outbox.`,
      );
      await handleVerifyCollaborationRuntime(collaborationSendEmail);
    } catch (error) {
      console.error(error);
      setCollaborationNotice("Provisioning impossible. Corrigez les droits, le quota ou la configuration e-mail avant de retenter.");
    } finally {
      setCollaborationProvisioning(false);
    }
  }

  async function handleSaveNativeIntake() {
    if (!selectedProjectId) {
      setNativeIntakeNotice("SÃ©lectionnez un projet Atlas avant de capturer un processus.");
      return;
    }
    const processName = nativeProcessName.trim();
    const useCaseTitle = nativeUseCaseTitle.trim();
    const ideaProblem = nativeIdeaProblem.trim();
    const ideaTarget = nativeIdeaTarget.trim();
    const kpis = parseNativeList(nativeKpis);
    if (!processName || !useCaseTitle || !ideaProblem || !ideaTarget || !kpis.length) {
      setNativeIntakeNotice("Processus, use case, probleme, cible et au moins un KPI sont requis.");
      return;
    }
    const contributorEmail = collaborationContributorEmail.trim().toLowerCase();
    if (selectedCollaborationGroup) {
      if (!contributorEmail || !isEmail(contributorEmail)) {
        setNativeIntakeNotice("Un e-mail contributeur valide est requis pour une contribution rattachee a un groupe.");
        return;
      }
      if (!selectedCollaborationGroup.memberEmails.includes(contributorEmail)) {
        setNativeIntakeNotice("Le contributeur doit faire partie de la liste d'e-mails du groupe selectionne.");
        return;
      }
    }
    setNativeIntakeSaving(true);
    setNativeIntakeNotice(null);
    try {
      const painPoints = parseNativeList(nativePainPoints);
      const tools = parseNativeList(nativeTools);
      const dependencies = parseNativeList(nativeIdeaDependencies);
      const finalOwner = selectedCollaborationGroup?.leadEmail ?? nativeOwner.trim();
      const collaborationEvidence = selectedCollaborationGroup
        ? {
            version: "longwalls.atlas-group-collaboration@1",
            groupId: selectedCollaborationGroup.id,
            groupName: selectedCollaborationGroup.name,
            groupLeadEmail: selectedCollaborationGroup.leadEmail,
            contributorEmail,
            memberEmails: selectedCollaborationGroup.memberEmails,
            ucOwner: selectedCollaborationGroup.leadEmail,
          }
        : null;
      const userEmails = Array.from(new Set([finalOwner, contributorEmail].filter(Boolean)));
      const ideaIntakeV2 = {
        version: "longwalls.atlas-idea-intake@2",
        sourceMode: "native_form",
        source: "native_form",
        provenance: "Studio native form",
        problem: ideaProblem,
        targetOutcome: ideaTarget,
        urgency: nativeIdeaUrgency,
        complexity: nativeIdeaComplexity,
        estimatedValue: nativeIdeaValue,
        dependencies,
        roadmapOnly: true,
        foundryBuildReady: false,
        jarvisBuildReady: false,
      };
      await saveRoadmapData(selectedProjectId, {
        processes: [
          {
            name: processName,
            matchKey: processName,
            raw: {
              source: "atlas_native_intake",
              ideaIntakeV2,
              collaboration: collaborationEvidence,
              description: nativeProcessDescription.trim(),
              painPoints,
              kpis,
              owner: finalOwner,
              reviewOwner: finalOwner,
              tools,
            },
          },
        ],
        useCases: [
          {
            name: useCaseTitle,
            linkedProcessKey: processName,
            raw: {
              source: "atlas_native_intake",
              ideaIntakeV2,
              collaboration: collaborationEvidence,
              outcome: nativeUseCaseOutcome.trim(),
              owner: finalOwner,
              reviewOwner: finalOwner,
              kpis,
            },
          },
        ],
        users: userEmails.map((email) => ({
          airtableRecordId: `atlas-user-${email.replace(/[^a-z0-9]+/g, "-")}`,
          raw: {
            Email: email,
            "Display Name": email,
            groupId: selectedCollaborationGroup?.id ?? null,
            groupName: selectedCollaborationGroup?.name ?? null,
          },
        })),
        replaceExisting: false,
      });
      const refreshed = await fetchRoadmapData(selectedProjectId);
      setLoadedProcesses(refreshed.processes);
      setLoadedUseCases(refreshed.useCases);
      setLoadedLinks(refreshed.links);
      setNativeIntakeNotice(
        selectedCollaborationGroup
          ? "Contribution enregistree. Le responsable du groupe est devenu UC Owner."
          : "Capture native enregistree. Airtable reste disponible uniquement comme bridge.",
      );
      setNativeProcessName("");
      setNativeProcessDescription("");
      setNativePainPoints("");
      setNativeKpis("");
      setNativeOwner("");
      setNativeTools("");
      setNativeUseCaseTitle("");
      setNativeUseCaseOutcome("");
      setNativeIdeaProblem("");
      setNativeIdeaTarget("");
      setNativeIdeaUrgency("high");
      setNativeIdeaComplexity("medium");
      setNativeIdeaValue("strategic");
      setNativeIdeaDependencies("");
      setCollaborationContributorEmail("");
    } catch (error) {
      console.error(error);
      setNativeIntakeNotice("Capture native impossible. VÃ©rifiez les droits projet et les migrations Atlas.");
    } finally {
      setNativeIntakeSaving(false);
    }
  }

  async function handleAddSegment() {
    if (!selectedProjectId) {
      setSegmentError("Sélectionnez un projet avant d'ajouter une application.");
      return;
    }
    const name = newSegmentForm.name.trim();
    if (!name) {
      setSegmentError("Le nom de l'application est requis.");
      return;
    }
    try {
      const segment = await createRoadmapSegment(selectedProjectId, {
        name,
        purpose: newSegmentForm.purpose.trim(),
        solutionType: newSegmentForm.solutionType.trim(),
        aiWhatItDoes: newSegmentForm.aiWhatItDoes.trim(),
        impact: newSegmentForm.impact.trim(),
      });
      setSegments((prev) => [...prev, segment]);
      setSelectedSegmentId(segment.id);
      setNewSegmentForm(EMPTY_SEGMENT_FORM);
      setSegmentError(null);
    } catch (error) {
      console.error(error);
      setSegmentError("Impossible d'ajouter l'application.");
    }
  }

  async function handleUpdateSegment() {
    if (!selectedSegmentId) {
      setSegmentError("Sélectionnez une application à mettre à jour.");
      return;
    }
    const name = segmentForm.name.trim();
    if (!name) {
      setSegmentError("Le nom de l'application est requis.");
      return;
    }
    try {
      const segment = await updateRoadmapSegment(selectedSegmentId, {
        name,
        purpose: segmentForm.purpose.trim(),
        solutionType: segmentForm.solutionType.trim(),
        aiWhatItDoes: segmentForm.aiWhatItDoes.trim(),
        impact: segmentForm.impact.trim(),
      });
      setSegments((prev) => prev.map((item) => (item.id === segment.id ? segment : item)));
      setSegmentError(null);
    } catch (error) {
      console.error(error);
      setSegmentError("Impossible de mettre à jour l'application.");
    }
  }

  async function handleDeleteSegment(segmentId: string) {
    try {
      await deleteRoadmapSegment(segmentId);
      setSegments((prev) => prev.filter((segment) => segment.id !== segmentId));
      setSelectedSegmentId((current) => (current === segmentId ? "" : current));
      setSegmentError(null);
    } catch (error) {
      console.error(error);
      setSegmentError("Impossible de supprimer l'application.");
    }
  }

  async function handleAssignUseCase(useCaseId: string, nextSegmentId: string) {
    const currentSegmentId = useCaseSegmentMap.get(useCaseId) ?? "";
    if (currentSegmentId === nextSegmentId) return;
    try {
      if (nextSegmentId) {
        await assignRoadmapSegmentUseCase(nextSegmentId, useCaseId);
      } else if (currentSegmentId) {
        await removeRoadmapSegmentUseCase(currentSegmentId, useCaseId);
      }

      setSegments((prev) =>
        prev.map((segment) => {
          const useCaseIds = segment.useCaseIds ?? [];
          const filtered = useCaseIds.filter((id) => id !== useCaseId);
          if (segment.id === nextSegmentId) {
            return {
              ...segment,
              useCaseIds: [...filtered, useCaseId],
            };
          }
          if (segment.id === currentSegmentId) {
            return {
              ...segment,
              useCaseIds: filtered,
            };
          }
          return segment;
        }),
      );
      setSegmentError(null);
    } catch (error) {
      console.error(error);
      setSegmentError("Impossible de mettre à jour l'affectation du use case.");
    }
  }

  return (
    <div className="content-stack">
      {hideHeader ? null : (
        <PageHeader
          title="Atlas"
          subtitle="Planifiez l'activation IA : de la vision stratégique à la mise en production."
        />
      )}

      <Card>
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-sm font-semibold text-slate-700">Projet de roadmap</div>
            <p className="text-sm text-slate-600">
              Associez la roadmap à un client et à un projet pour garder plusieurs initiatives séparées.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Client
              <SelectInput
                value={selectedCustomerId}
                onChange={(event) => setSelectedCustomerId(event.target.value)}
              >
                <option value="">Sélectionner…</option>
                {customerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Projet de roadmap
              <SelectInput
                value={selectedProjectId}
                onChange={(event) => setSelectedProjectId(event.target.value)}
                disabled={!projects.length}
              >
                <option value="">Sélectionner…</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </SelectInput>
            </label>
          </div>
          <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-end">
            <label className="flex flex-col gap-1 text-sm text-slate-700 sm:min-w-[260px] sm:flex-1">
              Nouveau projet
              <TextInput
                value={newProjectName}
                onChange={(event) => setNewProjectName(event.target.value)}
                placeholder="Roadmap IA 2024"
              />
            </label>
            <button type="button" className="button button-secondary button-sm w-full sm:w-auto" onClick={handleAddProject}>
              Créer le projet
            </button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
        <button
          type="button"
          className={`button button-sm w-full justify-center sm:w-auto ${activeTab === "native" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("native")}
        >
          Capture native
        </button>
        <button
          type="button"
          className={`button button-sm w-full justify-center sm:w-auto ${activeTab === "contributors" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("contributors")}
        >
          Groupes & idees
        </button>
        <button
          type="button"
          className={`button button-sm w-full justify-center sm:w-auto ${activeTab === "import" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("import")}
        >
          Import & synthèse
        </button>
        <button
          type="button"
          className={`button button-sm w-full justify-center sm:w-auto ${activeTab === "aiFields" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("aiFields")}
        >
          Champs IA
        </button>
      </div>

      {activeTab === "native" ? (
        <Card>
          <div className="flex flex-col gap-5">
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-200">Atlas native-first</div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">Capturez idee, process, pain points, KPI et use case sans Airtable.</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-200">
                Airtable reste un bridge d'import, de sync ou de migration. Le chemin canonique Atlas dÃ©marre ici:
                donnÃ©es natives, ownership clair, preuves exploitables par le scoring, le dossier sponsor et Foundry.
              </p>
            </div>
            {nativeIntakeNotice ? (
              <InlineAlert
                tone={nativeIntakeNotice.includes("impossible") || nativeIntakeNotice.includes("requis") ? "danger" : "success"}
                title="Capture native"
                description={nativeIntakeNotice}
              />
            ) : null}
            {loadedCollaborationGroups.length ? (
              <div className="grid gap-3 rounded-2xl border border-cyan-200 bg-cyan-50 p-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Groupe contributeur
                  <SelectInput
                    value={selectedCollaborationGroupId}
                    onChange={(event) => setSelectedCollaborationGroupId(event.target.value)}
                  >
                    <option value="">Sans groupe</option>
                    {loadedCollaborationGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name} - UC Owner {group.leadEmail}
                      </option>
                    ))}
                  </SelectInput>
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  E-mail du contributeur
                  <TextInput
                    value={collaborationContributorEmail}
                    onChange={(event) => setCollaborationContributorEmail(event.target.value)}
                    placeholder="contributeur@company.com"
                  />
                </label>
                <div className="rounded-xl border border-cyan-200 bg-white px-3 py-2 text-xs leading-5 text-slate-700">
                  Responsable final: {selectedCollaborationGroup?.leadEmail ?? (nativeOwner.trim() || "owner libre")}
                </div>
              </div>
            ) : null}
            <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Processus</div>
                  <p className="text-sm text-slate-500">Decrivez le fonctionnement actuel avec les signaux qui serviront a l'IA.</p>
                </div>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Nom du processus
                  <TextInput value={nativeProcessName} onChange={(event) => setNativeProcessName(event.target.value)} placeholder="Dispatch messengers Moka" />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Description du process
                  <textarea
                    className="input min-h-[96px]"
                    value={nativeProcessDescription}
                    onChange={(event) => setNativeProcessDescription(event.target.value)}
                    placeholder="Qui fait quoi, dans quel ordre, avec quelles contraintes..."
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Pain points
                  <textarea
                    className="input min-h-[84px]"
                    value={nativePainPoints}
                    onChange={(event) => setNativePainPoints(event.target.value)}
                    placeholder="Un par ligne ou sÃ©parÃ©s par virgules"
                  />
                </label>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Owner
                    <TextInput value={nativeOwner} onChange={(event) => setNativeOwner(event.target.value)} placeholder="COO / Ops manager" />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Outils / intÃ©grations
                    <TextInput value={nativeTools} onChange={(event) => setNativeTools(event.target.value)} placeholder="Google Maps, Supabase, ERP..." />
                  </label>
                </div>
              </div>
              <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Use case et KPI</div>
                  <p className="text-sm text-slate-500">Le use case est liÃ© au processus saisi et reste exploitable par segmentation.</p>
                </div>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Use case
                  <TextInput value={nativeUseCaseTitle} onChange={(event) => setNativeUseCaseTitle(event.target.value)} placeholder="Optimiser les tournÃ©es messengers" />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Outcome attendu
                  <textarea
                    className="input min-h-[96px]"
                    value={nativeUseCaseOutcome}
                    onChange={(event) => setNativeUseCaseOutcome(event.target.value)}
                    placeholder="RÃ©duire les km, Ã©quilibrer la charge, fiabiliser les preuves..."
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Probleme a resoudre
                  <textarea
                    className="input min-h-[84px]"
                    value={nativeIdeaProblem}
                    onChange={(event) => setNativeIdeaProblem(event.target.value)}
                    placeholder="Ce qui bloque le process aujourd'hui, avec impact metier observable..."
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Cible / etat voulu
                  <textarea
                    className="input min-h-[84px]"
                    value={nativeIdeaTarget}
                    onChange={(event) => setNativeIdeaTarget(event.target.value)}
                    placeholder="Ce que l'entreprise veut obtenir apres la roadmap AI..."
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  KPI
                  <textarea
                    className="input min-h-[84px]"
                    value={nativeKpis}
                    onChange={(event) => setNativeKpis(event.target.value)}
                    placeholder="CoÃ»t/km, SLA, preuves validÃ©es, taux de replanification..."
                  />
                </label>
                <div className="grid gap-3 md:grid-cols-3">
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Urgence
                    <SelectInput value={nativeIdeaUrgency} onChange={(event) => setNativeIdeaUrgency(event.target.value)}>
                      <option value="low">Basse</option>
                      <option value="medium">Moyenne</option>
                      <option value="high">Haute</option>
                      <option value="critical">Critique</option>
                    </SelectInput>
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Complexite
                    <SelectInput value={nativeIdeaComplexity} onChange={(event) => setNativeIdeaComplexity(event.target.value)}>
                      <option value="low">Faible</option>
                      <option value="medium">Moyenne</option>
                      <option value="high">Forte</option>
                      <option value="unknown">A qualifier</option>
                    </SelectInput>
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Valeur estimee
                    <SelectInput value={nativeIdeaValue} onChange={(event) => setNativeIdeaValue(event.target.value)}>
                      <option value="low">Faible</option>
                      <option value="medium">Moyenne</option>
                      <option value="high">Haute</option>
                      <option value="strategic">Strategique</option>
                    </SelectInput>
                  </label>
                </div>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Dependances
                  <textarea
                    className="input min-h-[72px]"
                    value={nativeIdeaDependencies}
                    onChange={(event) => setNativeIdeaDependencies(event.target.value)}
                    placeholder="Donnees, outils, owners, integrations, contraintes. Un par ligne ou separes par virgules."
                  />
                </label>
                <button
                  type="button"
                  className="button button-primary button-sm w-full justify-center"
                  onClick={() => void handleSaveNativeIntake()}
                  disabled={nativeIntakeSaving || !selectedProjectId}
                >
                  {nativeIntakeSaving ? "Enregistrement..." : "Enregistrer dans Atlas"}
                </button>
                <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-3 text-xs leading-5 text-slate-700">
                  Chemin nominal 52R-1: capture native Roadmap AI, provenance, dedupe, scoring, process design,
                  dossier sponsor, puis Foundry. Le bridge Airtable ne devient jamais obligatoire.
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {activeTab === "contributors" ? (
        <Card>
          <div className="flex flex-col gap-5">
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-950 p-5 text-white shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-200">Atlas group collaboration</div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">Creez des groupes, nommez un responsable et collectez les idees.</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-200">
                Le responsable devient le UC Owner des contributions rattachees au groupe. Cette version structure les e-mails et l'ownership;
                elle provisionne les comptes et invitations avec egress bloque par defaut.
              </p>
            </div>
            {collaborationNotice ? (
              <InlineAlert
                tone={collaborationNotice.includes("impossible") || collaborationNotice.includes("invalide") ? "danger" : "success"}
                title="Groupes Atlas"
                description={collaborationNotice}
              />
            ) : null}
            <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
              <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Nouveau groupe</div>
                  <p className="text-sm text-slate-500">
                    Collez la liste des e-mails, puis designez le responsable qui portera l'ownership des UC.
                  </p>
                </div>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Nom du groupe
                  <TextInput
                    value={collaborationGroupName}
                    onChange={(event) => setCollaborationGroupName(event.target.value)}
                    placeholder="Operations Moka"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Responsable du groupe
                  <TextInput
                    value={collaborationLeadEmail}
                    onChange={(event) => setCollaborationLeadEmail(event.target.value)}
                    placeholder="owner@company.com"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Liste d'e-mails membres
                  <textarea
                    className="input min-h-[120px]"
                    value={collaborationMemberEmails}
                    onChange={(event) => setCollaborationMemberEmails(event.target.value)}
                    placeholder="membre1@company.com&#10;membre2@company.com"
                  />
                </label>
                <button
                  type="button"
                  className="button button-primary button-sm w-full justify-center"
                  onClick={() => void handleSaveCollaborationGroup()}
                  disabled={collaborationSaving || !selectedProjectId}
                >
                  {collaborationSaving ? "Enregistrement..." : "Enregistrer le groupe"}
                </button>
              </div>
              <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Groupes actifs</div>
                  <p className="text-sm text-slate-500">
                    Selectionnez ensuite un groupe dans la capture native: le responsable sera assigne comme UC Owner.
                  </p>
                </div>
                {loadedCollaborationGroups.length ? (
                  <div className="grid gap-3">
                    {loadedCollaborationGroups.map((group) => (
                      <button
                        key={group.id}
                        type="button"
                        className={`rounded-2xl border p-4 text-left transition ${
                          selectedCollaborationGroupId === group.id
                            ? "border-cyan-500 bg-cyan-50 shadow-sm"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        onClick={() => setSelectedCollaborationGroupId(group.id)}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-semibold text-slate-900">{group.name}</div>
                          <span className="rounded-full bg-slate-900 px-2 py-1 text-xs font-semibold text-white">
                            {group.memberEmails.length} e-mails
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-slate-600">Responsable / UC Owner: {group.leadEmail}</div>
                        <div className="mt-2 text-xs text-slate-500">{group.memberEmails.join(", ")}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title="Aucun groupe cree"
                    description="Creez un groupe pour structurer la collecte d'idees et assigner automatiquement le UC Owner."
                  />
                )}
              </div>
            </div>
            <div className="grid gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 lg:grid-cols-[1fr_1fr]">
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Provisioning comptes & invitations</div>
                  <p className="mt-1 text-sm leading-6 text-slate-700">
                    Par defaut, aucun e-mail n'est envoye. Atlas cree les comptes, les memberships projet et une outbox de tokens
                    pour eviter tout egress non controle.
                  </p>
                </div>
                <label className="flex items-start gap-3 rounded-xl border border-amber-200 bg-white p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={collaborationSendEmail}
                    onChange={(event) => setCollaborationSendEmail(event.target.checked)}
                  />
                  <span>
                    Envoyer via le relais e-mail configure. Cette option reste bloquee si
                    <code className="mx-1 rounded bg-slate-100 px-1">ATLAS_INVITATION_EMAIL_ENABLED</code>
                    ou le webhook ne sont pas prets.
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="button button-primary button-sm"
                    onClick={() => void handleProvisionCollaborationGroups()}
                    disabled={collaborationProvisioning || !selectedProjectId || !loadedCollaborationGroups.length}
                  >
                    {collaborationProvisioning ? "Provisioning..." : "Provisionner comptes & invitations"}
                  </button>
                  <button
                    type="button"
                    className="button button-secondary button-sm"
                    onClick={() => void handleVerifyCollaborationRuntime()}
                    disabled={collaborationRuntimeLoading || !selectedProjectId}
                  >
                    {collaborationRuntimeLoading ? "Verification..." : "Verifier runtime"}
                  </button>
                </div>
              </div>
              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900">Etat runtime</div>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      collaborationRuntime?.status === "ready"
                        ? "bg-emerald-100 text-emerald-800"
                        : collaborationRuntime?.status === "ready_with_warnings"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {collaborationRuntime?.status ?? "non verifie"}
                  </span>
                </div>
                {collaborationProvisioningResult ? (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg bg-slate-50 p-2">Comptes crees: {collaborationProvisioningResult.summary.createdAccounts}</div>
                    <div className="rounded-lg bg-slate-50 p-2">Membres projet: {collaborationProvisioningResult.summary.projectMembersCreated}</div>
                    <div className="rounded-lg bg-slate-50 p-2">E-mails envoyes: {collaborationProvisioningResult.summary.emailSent}</div>
                    <div className="rounded-lg bg-slate-50 p-2">Outbox tokens: {collaborationProvisioningResult.summary.outboxTokens}</div>
                  </div>
                ) : (
                  <p>Aucun provisioning execute sur ce projet dans cette session.</p>
                )}
                {collaborationRuntime ? (
                  <div className="space-y-2">
                    <p>
                      Cibles: {collaborationRuntime.summary.targetEmailCount} | Comptes: {collaborationRuntime.summary.accountCount} |
                      Memberships: {collaborationRuntime.summary.projectMemberCount} | Invites: {collaborationRuntime.summary.pendingInviteCount}
                    </p>
                    <p>
                      E-mail runtime: {collaborationRuntime.emailRuntime.mode} / provider {collaborationRuntime.emailRuntime.provider}
                    </p>
                    {collaborationRuntime.blockers.length ? (
                      <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-800">
                        {collaborationRuntime.blockers.join(" ")}
                      </div>
                    ) : null}
                    {collaborationRuntime.warnings.length ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-800">
                        {collaborationRuntime.warnings.join(" ")}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {activeTab === "import" ? (
        <>
          <Card>
            <div className="flex flex-col gap-4">
              <div>
                <div className="text-sm font-semibold text-slate-700">Import RI-7 / RI-8</div>
                <p className="text-sm text-slate-600">
                  Importez vos tables Processes (RI-7) et Use Cases (RI-8) depuis Airtable (CSV ou API).
                </p>
              </div>
              {importError ? (
                <InlineAlert tone="danger" title="Import impossible" description={importError} />
              ) : null}
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="flex flex-col gap-3">
                  <div className="text-sm font-semibold text-slate-700">CSV</div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Processes (RI-7)</label>
                  <input className="input" type="file" accept=".csv" onChange={handleCsvUpload} />
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Use cases (RI-8)</label>
                  <input className="input" type="file" accept=".csv" onChange={handleUseCasesCsvUpload} />
                </div>
                <div className="flex flex-col gap-3">
                  <div className="text-sm font-semibold text-slate-700">Airtable API</div>
                  <p className="text-xs text-slate-500">
                    Ces paramètres se configurent dans Workspace settings, par client et projet de roadmap.
                  </p>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Base ID
                    <TextInput value={airtableBaseId} placeholder="appXXXXXXXXXXXXXX" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Table Processes (RI-7)
                    <TextInput value={airtableProcessesTable} placeholder="Processes" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    View Processes (optionnel)
                    <TextInput value={airtableProcessesView} placeholder="All records" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Table Use Cases (RI-8)
                    <TextInput value={airtableUseCasesTable} placeholder="Use Cases" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    View Use Cases (optionnel)
                    <TextInput value={airtableUseCasesView} placeholder="All records" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Table Users (optionnel)
                    <TextInput value={airtableUsersTable} placeholder="Users" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    View Users (optionnel)
                    <TextInput value={airtableUsersView} placeholder="All records" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Table Apps (optionnel)
                    <TextInput value={airtableAppsTable} placeholder="Applications" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    View Apps (optionnel)
                    <TextInput value={airtableAppsView} placeholder="All records" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Table Integrations (optionnel)
                    <TextInput value={airtableIntegrationsTable} placeholder="Integrations" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    View Integrations (optionnel)
                    <TextInput value={airtableIntegrationsView} placeholder="All records" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Table Technical Assessments (optionnel)
                    <TextInput value={airtableTechnicalAssessmentsTable} placeholder="Technical Assessments" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    View Technical Assessments (optionnel)
                    <TextInput value={airtableTechnicalAssessmentsView} placeholder="All records" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Table Data Risk Checklists (optionnel)
                    <TextInput value={airtableDataRiskChecklistsTable} placeholder="Data Risk Checklists" disabled />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    View Data Risk Checklists (optionnel)
                    <TextInput value={airtableDataRiskChecklistsView} placeholder="All records" disabled />
                  </label>
                  <div className="text-xs text-slate-500">
                    Le token Airtable est stocké dans Workspace settings et n’est jamais affiché ici.
                  </div>
                  <button
                    type="button"
                    className="button button-primary button-sm w-full justify-center sm:w-auto sm:self-start"
                    onClick={() => void handleAirtableImport()}
                    disabled={isImporting}
                  >
                    {isImporting ? "Import en cours..." : "Importer Airtable"}
                  </button>
                </div>
              </div>
              {headers.length ? (
                <div className="rounded border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-700">
                      {rows.length} processus importés • {effectiveUseCaseRows.length} use cases importés
                    </div>
                    <button type="button" className="button button-secondary button-sm" onClick={handleClearImport}>
                      Réinitialiser
                    </button>
                  </div>
                  {missingMapping ? (
                    <InlineAlert
                      tone="warning"
                      title="Colonnes RI-7 / RI-8 non détectées"
                      description={`Sélectionnez les colonnes de processus et use case. Colonnes disponibles : ${headers.join(", ")}`}
                    />
                  ) : null}
                  {mappingValidationError ? (
                    <InlineAlert tone="danger" title="Mapping Airtable invalide" description={mappingValidationError} />
                  ) : null}
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      Colonne Processus (RI-7) - nom affiché
                      <SelectInput value={processLabelField} onChange={(event) => setProcessLabelField(event.target.value)}>
                        <option value="">Sélectionner…</option>
                        {headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </SelectInput>
                    </label>
                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      Colonne Processus (RI-7) - clé de matching
                      <SelectInput value={processMatchField} onChange={(event) => setProcessMatchField(event.target.value)}>
                        <option value="">Sélectionner…</option>
                        <option value={AIRTABLE_RECORD_ID_FIELD}>Airtable Record ID</option>
                        {processMatchOptions.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </SelectInput>
                    </label>
                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      Colonne Use case (RI-8)
                      <SelectInput value={useCaseField} onChange={(event) => setUseCaseField(event.target.value)}>
                        <option value="">Sélectionner…</option>
                        {useCaseFieldOptions.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </SelectInput>
                    </label>
                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      Colonne Processus (RI-7) côté Use Cases
                      <SelectInput
                        value={useCaseProcessField}
                        onChange={(event) => setUseCaseProcessField(event.target.value)}
                      >
                        <option value="">Sélectionner…</option>
                        {useCaseFieldOptions.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </SelectInput>
                    </label>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="button button-primary button-sm w-full sm:w-auto"
                      onClick={() => void persistRoadmapData()}
                      disabled={!selectedProjectId || missingMapping}
                    >
                      Enregistrer la base en Postgres
                    </button>
                    <button
                      type="button"
                      className="button button-secondary button-sm w-full sm:w-auto"
                      onClick={() => void handleApplyMapping()}
                      disabled={!selectedProjectId || missingMapping || Boolean(mappingValidationError) || isApplyingMapping}
                    >
                      {isApplyingMapping ? "Mapping..." : "Appliquer le mapping IA"}
                    </button>
                  </div>
                  {linkSummary ? (
                    <div className="text-xs text-slate-500">
                      {linkSummary.processCount} processus • {linkSummary.useCaseCount} use cases •{" "}
                      {linkSummary.linkCount} liens créés • {linkSummary.missingUseCaseLinks} use case(s) sans lien
                      malgré des valeurs Airtable
                    </div>
                  ) : null}
                  {isSuggestingMapping ? (
                    <div className="text-xs text-slate-500">Suggestion de mapping IA en cours...</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Card>

          <Dialog
            open={showOverrideDialog}
            onOpenChange={(open) => {
              setShowOverrideDialog(open);
              if (!open) {
                setOverrideReason("");
                setOverrideError(null);
              }
            }}
          >
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Override blockers</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm text-slate-700">
                <p>
                  Renseignez la raison de la dérogation. L'action est auditée et permettra de poursuivre malgré les blocages.
                </p>
                <textarea
                  className="min-h-[120px] w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  value={overrideReason}
                  onChange={(event) => setOverrideReason(event.target.value)}
                  placeholder="Motif de validation malgré les blocages..."
                />
                {overrideError ? <div className="text-xs text-rose-600">{overrideError}</div> : null}
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => setShowOverrideDialog(false)}
                    disabled={overrideLoading}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={() => void handleOverrideQualityGate()}
                    disabled={overrideLoading || !overrideReason.trim()}
                  >
                    {overrideLoading ? "Validation..." : "Valider malgré les blocages"}
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Card>
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-700">Data Quality Audit</div>
                  <p className="text-sm text-slate-600">
                    Vérifiez la cohérence des imports RI-7 / RI-8 avant la revue (blocages, warnings, doublons).
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="button button-secondary button-sm"
                    onClick={handleDownloadAudit}
                    disabled={!qualityAudit}
                  >
                    Télécharger JSON
                  </button>
                  {canOverrideQualityGate ? (
                    <button
                      type="button"
                      className="button button-secondary button-sm"
                      onClick={() => setShowOverrideDialog(true)}
                      disabled={!qualityAudit}
                    >
                      Override blockers
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="button button-primary button-sm"
                    onClick={() => void handleRunQualityAudit()}
                    disabled={!selectedProjectId || isQualityAuditLoading}
                  >
                    {isQualityAuditLoading ? "Audit..." : "Lancer l'audit"}
                  </button>
                </div>
              </div>
              {qualityAuditMigration ? (
                <div className="space-y-4">
                  <AtlasMigrationsRequiredCallout details={qualityAuditMigration} />
                  <EmptyState
                    title="Migrations nécessaires"
                    description="Appliquez les migrations Prisma pour lancer l'audit qualité."
                  />
                </div>
              ) : (
                <>
                  {qualityAuditError ? (
                    <InlineAlert tone="danger" title="Audit qualité" description={qualityAuditError} />
                  ) : null}
                  {qualityAudit ? (
                    <div className="rounded border border-slate-200 p-3">
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-semibold ${stopReasonBadgeClass(
                            qualityAudit.gate?.status ?? qualityAudit.stopReason,
                          )}`}
                        >
                          {qualityAudit.gate?.status ?? qualityAudit.stopReason}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(qualityAudit.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                        <div className="rounded border border-slate-200 p-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Volumes</div>
                          <div className="mt-2 space-y-1 text-sm text-slate-600">
                            <div>{qualityAudit.summary.processCount} processus</div>
                            <div>{qualityAudit.summary.useCaseCount} use cases</div>
                            <div>{qualityAudit.summary.applicationCount} applications</div>
                          </div>
                        </div>
                        <div className="rounded border border-slate-200 p-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Alertes</div>
                          <div className="mt-2 space-y-1 text-sm text-slate-600">
                            <div>{qualityAudit.summary.blockerCount} blocage(s)</div>
                            <div>{qualityAudit.summary.warningCount} warning(s)</div>
                            <div>{qualityAudit.summary.readyApplicationsCount} application(s) prêtes</div>
                          </div>
                        </div>
                      </div>
                      {qualityAudit.gate?.override ? (
                        <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                          <div className="font-semibold">Override appliqué</div>
                          <div className="mt-1 space-y-1 text-amber-700/90">
                            <div>Raison: {qualityAudit.gate.override.reason}</div>
                            <div>
                              Par: {qualityAudit.gate.override.createdByEmail ?? qualityAudit.gate.override.createdByUserId ?? "—"}
                            </div>
                            <div>Le: {new Date(qualityAudit.gate.override.createdAt).toLocaleString()}</div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">Aucun audit enregistré pour ce projet.</div>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      Sévérité
                      <SelectInput
                        value={auditSeverityFilter}
                        onChange={(event) => setAuditSeverityFilter(event.target.value)}
                      >
                        <option value="ALL">Toutes</option>
                        <option value="BLOCKER">Blocages</option>
                        <option value="WARNING">Warnings</option>
                      </SelectInput>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      Type
                      <SelectInput value={auditEntityFilter} onChange={(event) => setAuditEntityFilter(event.target.value)}>
                        <option value="ALL">Tous</option>
                        <option value="PROCESS">Processus</option>
                        <option value="USE_CASE">Use cases</option>
                        <option value="APPLICATION">Applications</option>
                      </SelectInput>
                    </label>
                  </div>
                  <div className="overflow-x-auto">
                    {filteredAuditIssues.length ? (
                      <table className="min-w-full text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-2 py-2 text-left">Sévérité</th>
                            <th className="px-2 py-2 text-left">Type</th>
                            <th className="px-2 py-2 text-left">Nom</th>
                            <th className="px-2 py-2 text-left">Message</th>
                            <th className="px-2 py-2 text-left">Suggestion</th>
                            <th className="px-2 py-2 text-left">Lien</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {filteredAuditIssues.map((issue, index) => {
                            const href =
                              issue.deepLink ||
                              (issue.entityType === "USE_CASE"
                                ? (() => {
                                    const processId = useCaseToProcessId.get(issue.entityId);
                                    return processId
                                      ? `/ai_roadmap/review?processId=${processId}&useCaseId=${issue.entityId}`
                                      : `/ai_roadmap/review?useCaseId=${issue.entityId}`;
                                  })()
                                : issue.entityType === "PROCESS"
                                  ? `/ai_roadmap/review?processId=${issue.entityId}`
                                  : issue.entityType === "APPLICATION"
                                    ? `/ai_roadmap/final-review`
                                    : "");
                            return (
                              <tr key={`${issue.entityId}-${issue.code}-${index}`}>
                                <td className="px-2 py-2 text-xs font-semibold">{issue.severity}</td>
                                <td className="px-2 py-2 text-xs text-slate-600">{issue.entityType}</td>
                                <td className="px-2 py-2 text-xs text-slate-700">{issue.entityName ?? "—"}</td>
                                <td className="px-2 py-2 text-slate-700">{issue.message}</td>
                                <td className="px-2 py-2 text-xs text-slate-500">{issue.suggestedFix ?? "—"}</td>
                                <td className="px-2 py-2 text-xs">
                                  {href ? (
                                    <a className="text-blue-600 hover:underline" href={href}>
                                      Ouvrir
                                    </a>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-sm text-slate-500">Aucun problème à afficher.</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-700">AI Synthesis</div>
                  <p className="text-sm text-slate-600">
                    Synthèse exécutive post-import pour préparer la 1ère revue (priorités, risques, actions).
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="button button-secondary button-sm"
                    onClick={handleDownloadSynthesis}
                    disabled={!synthesis}
                  >
                    Télécharger JSON
                  </button>
                  <button
                    type="button"
                    className="button button-primary button-sm"
                    onClick={() => void handleRunSynthesis()}
                    disabled={!selectedProjectId || isSynthesisLoading}
                  >
                    {isSynthesisLoading ? "Synthèse..." : "Lancer la synthèse"}
                  </button>
                </div>
              </div>
              {synthesisMigration ? (
                <div className="space-y-4">
                  <AtlasMigrationsRequiredCallout details={synthesisMigration} />
                  <EmptyState
                    title="Migrations nécessaires"
                    description="Appliquez les migrations Prisma pour générer la synthèse Atlas."
                  />
                </div>
              ) : (
                <>
                  {synthesisError ? (
                    <InlineAlert tone="danger" title="Synthèse IA" description={synthesisError} />
                  ) : null}
                  {synthesis ? (
                    <div className="rounded border border-slate-200 p-3">
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-semibold ${stopReasonBadgeClass(
                            synthesis.stopReason,
                          )}`}
                        >
                          {synthesis.stopReason}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(synthesis.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-3 space-y-3 text-sm text-slate-700">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Résumé</div>
                          <p className="mt-1 text-sm text-slate-700">{synthesis.synthesis.summary}</p>
                        </div>
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Priorités</div>
                          {synthesis.synthesis.priorities.length ? (
                            <ul className="mt-1 space-y-2 text-sm text-slate-700">
                              {synthesis.synthesis.priorities.map((priority, index) => (
                                <li key={`${priority.title}-${index}`} className="rounded border border-slate-200 p-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-semibold text-slate-600">{priority.priority}</span>
                                    <span className="font-semibold text-slate-700">{priority.title}</span>
                                  </div>
                                  <div className="text-xs text-slate-600">{priority.rationale}</div>
                                  {priority.useCaseIds.length ? (
                                    <div className="text-xs text-slate-500">
                                      Use cases : {priority.useCaseIds.join(", ")}
                                    </div>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="mt-1 text-sm text-slate-500">Aucune priorité proposée.</div>
                          )}
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Risques</div>
                            {synthesis.synthesis.risks.length ? (
                              <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                                {synthesis.synthesis.risks.map((risk, index) => (
                                  <li key={`${risk}-${index}`}>{risk}</li>
                                ))}
                              </ul>
                            ) : (
                              <div className="mt-1 text-sm text-slate-500">Aucun risque listé.</div>
                            )}
                          </div>
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Prochaines étapes
                            </div>
                            {synthesis.synthesis.nextSteps.length ? (
                              <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                                {synthesis.synthesis.nextSteps.map((step, index) => (
                                  <li key={`${step}-${index}`}>{step}</li>
                                ))}
                              </ul>
                            ) : (
                              <div className="mt-1 text-sm text-slate-500">Aucune étape listée.</div>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-slate-500">
                          Confiance : {synthesis.synthesis.confidence ?? "n/a"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">Aucune synthèse IA disponible pour ce projet.</div>
                  )}
                  {synthesis?.questionsForFirstReview?.length ? (
                    <div className="rounded border border-slate-200 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Questions pour la 1ère revue
                      </div>
                      <ul className="mt-2 space-y-2 text-sm text-slate-700">
                        {synthesis.questionsForFirstReview.map((question, index) => (
                          <li key={`${question.question}-${index}`}>
                            <div className="font-semibold">{question.question}</div>
                            {question.rationale ? (
                              <div className="text-xs text-slate-500">{question.rationale}</div>
                            ) : null}
                            {question.entityIds?.length ? (
                              <div className="text-xs text-slate-500">IDs: {question.entityIds.join(", ")}</div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {synthesis?.duplicatesOrInconsistencies?.length ? (
                    <div className="rounded border border-slate-200 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Doublons & incohérences
                      </div>
                      <ul className="mt-2 space-y-2 text-sm text-slate-700">
                        {synthesis.duplicatesOrInconsistencies.map((issue, index) => (
                          <li key={`${issue.description}-${index}`}>
                            <div className="font-semibold">{issue.description}</div>
                            <div className="text-xs text-slate-500">
                              {issue.type}
                              {issue.entityType ? ` • ${issue.entityType}` : ""}
                              {issue.entityIds?.length ? ` • ${issue.entityIds.join(", ")}` : ""}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </Card>
        </>
      ) : null}

      {activeTab === "aiFields" ? (
        <Card>
          {analysisFieldsMigration ? (
            <div className="space-y-4">
              <AtlasMigrationsRequiredCallout details={analysisFieldsMigration} />
              <EmptyState
                title="Migrations nécessaires"
                description="Appliquez les migrations Prisma pour configurer les champs IA."
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-sm font-semibold text-slate-700">Champs analysés par l’IA</div>
                <p className="text-sm text-slate-600">
                  Sélectionnez les champs à envoyer au modèle lors de la génération des briefs.
                </p>
              </div>
              <div>
                <button type="button" className="button button-secondary button-sm" onClick={handleApplyStandardFields}>
                  Standard par défaut
                </button>
              </div>
              <div className="space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Processus</div>
                <div className="grid gap-2 md:grid-cols-2">
                  {PROCESS_AI_FIELDS.map((field) => (
                    <label key={field.key} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={analysisProcessFields.includes(field.key)}
                        onChange={() => toggleSelection(field.key, analysisProcessFields, setAnalysisProcessFields)}
                      />
                      {field.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Use cases</div>
                <div className="grid gap-2 md:grid-cols-2">
                  {USE_CASE_AI_FIELDS.map((field) => (
                    <label key={field.key} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={analysisUseCaseFields.includes(field.key)}
                        onChange={() => toggleSelection(field.key, analysisUseCaseFields, setAnalysisUseCaseFields)}
                      />
                      {field.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={analysisIncludeRawFields}
                    onChange={(event) => setAnalysisIncludeRawFields(event.target.checked)}
                  />
                  Inclure les champs bruts (raw)
                </div>
                {analysisIncludeRawFields ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Raw processus
                      </div>
                      <div className="grid gap-2">
                        {processRawFieldOptions.length ? (
                          processRawFieldOptions.map((field) => (
                            <label key={field} className="flex items-center gap-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={analysisProcessRawFields.includes(field)}
                                onChange={() =>
                                  toggleSelection(field, analysisProcessRawFields, setAnalysisProcessRawFields)
                                }
                              />
                              {field}
                            </label>
                          ))
                        ) : (
                          <div className="text-xs text-slate-500">Aucun champ raw détecté.</div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Raw use cases
                      </div>
                      <div className="grid gap-2">
                        {useCaseRawFieldOptions.length ? (
                          useCaseRawFieldOptions.map((field) => (
                            <label key={field} className="flex items-center gap-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={analysisUseCaseRawFields.includes(field)}
                                onChange={() =>
                                  toggleSelection(field, analysisUseCaseRawFields, setAnalysisUseCaseRawFields)
                                }
                              />
                              {field}
                            </label>
                          ))
                        ) : (
                          <div className="text-xs text-slate-500">Aucun champ raw détecté.</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              {analysisFieldsError ? (
                <InlineAlert tone="danger" title="Champs IA" description={analysisFieldsError} />
              ) : null}
              <div className="text-xs text-slate-500">
                {analysisFieldsSaving
                  ? "Enregistrement des choix..."
                  : "Les choix sont enregistrés automatiquement pour ce projet."}
              </div>
            </div>
          )}
        </Card>
      ) : null}

    </div>
  );
}
